import {useCallback, useRef} from 'react'
import debounce from 'lodash/debounce.js'
import classNames from 'classnames'

import api from '../api.js'
import {getState, updateForm, formsSelector, useAutoForm} from '../../store.js'
import Icon from '../icons/Icon.js'
import TextInput from './TextInput.js'
import Zropdown, {useZropdown} from './Zropdown.js'
import {setPO} from '../../data/pos.js'
import POStatus from './POStatus.js'

export function getInitialForm() {
  return {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    isLoading: false,
    serverError: null,
  }
}

export async function searchPOs(
  dropdown,
  excludedPOIDs = [],
  queryParams = {},
) {
  const taskQueue = searchPOs.taskQueue || {}
  searchPOs.taskQueue = taskQueue

  taskQueue[dropdown] = {}

  try {
    const form = formsSelector(getState())[dropdown]

    if (!form) {
      return
    }

    const text = form.text.trim()

    if (!text) {
      updateForm(dropdown, {
        results: [],
        selectedIndex: 0,
        hasFoundNothing: false,
        isLoading: false,
        serverError: null,
      })

      return
    }

    updateForm(dropdown, {isLoading: true, serverError: null})

    const limit = 15 + excludedPOIDs.length

    const {
      json: {purchase_order: pos},
    } = await api.get('/purchase_order', {
      limit,
      search: form.text,
      ...queryParams,
    })

    if (searchPOs.taskQueue[dropdown] !== taskQueue[dropdown]) {
      return
    }

    const results = pos.filter(({po_id}) => !excludedPOIDs.includes(po_id))

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
      isLoading: false,
    })
  } catch (err) {
    updateForm(dropdown, {
      results: [],
      selectedIndex: 0,
      serverError: err.message,
      isLoading: false,
    })
  } finally {
    if (searchPOs.taskQueue[dropdown] === taskQueue[dropdown]) {
      delete taskQueue[dropdown]
    }
  }
}

/**
 * Search filter for Purchase Orders
 *
 * @param {Object} props
 * @param {String|Function} [props.label]
 * @param {String} [props.placeholder]
 * @param {String} props.dropdown
 * @param {Function} props.onSelect
 * @param {String[]} [props.excludedPOIDs]
 * @param {Object} [props.queryParams]
 * @param {Boolean} [props.autoFocus]
 * @param {Function} [props.onBlur]
 * @returns {React.ReactNode}
 */
export default function POFilter({
  label,
  placeholder,
  dropdown,
  onSelect,
  excludedPOIDs,
  queryParams,
  autoFocus,
  onBlur,
}) {
  const id = `${dropdown}__input`
  placeholder = placeholder || 'Search for a PO ID...'

  const debouncedSearchOrders = useCallback(
    debounce(() => searchPOs(dropdown, excludedPOIDs, queryParams), 400),
    [dropdown, excludedPOIDs, queryParams],
  )

  const ref = useRef()

  const form = useAutoForm(dropdown, getInitialForm())

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: () => debouncedSearchOrders(),
      onSelect: (po) => {
        setPO(po)

        onSelect(po)

        onChangeInternal('')
      },
    })

  return (
    <div className="outerwrap--order-search-zropdown">
      {label && (
        <label className="label--order-search" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="wrap--order-search" ref={ref}>
        <label className="label--input-prefix label--search" htmlFor={id}>
          <Icon name="search" className="icon--input-prefix" />
          <TextInput
            className="input--search input--lighter inline-block v-align-middle"
            id={id}
            value={form.text}
            placeholder={placeholder}
            onChange={(value) => onChangeInternal(value)}
            onBlur={(event) => onBlur && onBlur(event)}
            autoFocus={autoFocus}
          />
        </label>
      </div>
      <Zropdown
        parentRef={ref}
        dropdown={dropdown}
        form={form}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
      >
        {form.isLoading && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <span className="spinner--sm v-align-middle" />
            <strong className="inline-block v-align-middle fs-00 op-75 margin-left-5">
              Loading...
            </strong>
          </li>
        )}
        {form.results.map((po, index) => (
          <li key={po.po_id} className="list__item list__item--dropdown">
            <button
              className={classNames(
                'btn--link list__link list__link--dropdown flex--justify-nowrap',
                {
                  'list__link--dropdown-arrow-focus':
                    index === form.selectedIndex,
                },
              )}
              onClick={() => onSelectInternal(po)}
            >
              <div>
                <strong className="block">{po.po_id}</strong>
                <span className="block unbold">
                  Status: <POStatus po={po} />
                </span>
              </div>
            </button>
          </li>
        ))}
        {form.hasFoundNothing && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <em>No POs were found</em>
          </li>
        )}
      </Zropdown>
    </div>
  )
}
